import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { productMovementsAPI, productsAPI, apiUtils } from '../services/api';

const ProductMovements = () => {
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMovement, setEditingMovement] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    product_id: '',
    source: '',
    movement_type: '',
    refno: '',
    comment: '',
    qty: '',
  });

  // Movement source options with their corresponding movement types
  const movementOptions = [
    { source: 'Delivery', movement_type: 'IN' },
    { source: 'Transmittal (IN)', movement_type: 'IN' },
    { source: 'Transmittal (OUT)', movement_type: 'OUT' },
    { source: 'Waste', movement_type: 'OUT' },
    { source: 'Shrinkage', movement_type: 'OUT' },
  ];

  // Define loadProducts before using it in useEffect
  const loadProducts = useCallback(async () => {
    try {
      const response = await productsAPI.list();
      const newProducts = apiUtils.formatResponse(response) || [];
      setProducts(newProducts);
      
      // If we have a selected product, ensure it's updated with the latest data
      setSelectedProduct(prevSelected => {
        if (prevSelected) {
          const updatedSelectedProduct = newProducts.find(p => p.id === prevSelected.id);
          return updatedSelectedProduct || prevSelected;
        }
        return prevSelected;
      });
    } catch (error) {
      setError(apiUtils.handleError(error));
    }
  }, []); // No dependencies needed

  useEffect(() => {
    loadProducts();
  }, [loadProducts]); // Now loadProducts is memoized with useCallback

  useEffect(() => {
    if (selectedProduct && selectedProduct.id) {
      loadMovements(selectedProduct.id);
    }
  }, [selectedProduct?.id]); // Only trigger when the product ID changes, not other properties

  const loadMovements = async (productId) => {
    if (!productId || productId === undefined) {
      console.warn('loadMovements called without productId:', productId);
      return;
    }
    
    setLoading(true);
    try {
      console.log('Loading movements for product:', productId);
      const response = await productMovementsAPI.getByProduct(productId);
      const movementsData = apiUtils.formatResponse(response) || [];
      console.log('Loaded movements:', movementsData.length, 'movements');
      setMovements(movementsData);
    } catch (error) {
      console.error('Error loading movements:', error);
      setError(apiUtils.handleError(error));
      // Don't clear movements on error, just show the error
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh the selected product's current stock
  const refreshSelectedProduct = async () => {
    if (selectedProduct && selectedProduct.id) {
      try {
        console.log('Refreshing product data for:', selectedProduct.id);
        const response = await productsAPI.fetch(selectedProduct.id);
        const updatedProduct = apiUtils.formatResponse(response);
        if (updatedProduct && updatedProduct.id) {
          console.log('API Response - Updated product on_hand:', updatedProduct.on_hand);
          console.log('Current selectedProduct.on_hand before update:', selectedProduct.on_hand);
          // Ensure on_hand is properly set, fallback to current value if undefined
          const productWithOnHand = {
            ...updatedProduct,
            on_hand: updatedProduct.on_hand !== undefined ? updatedProduct.on_hand : selectedProduct.on_hand || 0
          };
          console.log('Final productWithOnHand.on_hand to be set:', productWithOnHand.on_hand);
          setSelectedProduct(productWithOnHand);
          console.log('setSelectedProduct called with on_hand:', productWithOnHand.on_hand);
          // Also update the product in the products list
          setProducts(prevProducts => 
            prevProducts.map(p => 
              p.id === productWithOnHand.id ? productWithOnHand : p
            )
          );
        } else {
          // If we can't fetch the updated product, at least keep the current selection
          console.warn('Could not fetch updated product data, keeping current selection');
        }
      } catch (error) {
        console.error('Error refreshing product data:', error);
        // Don't clear the selected product on error, just log it
      }
    } else {
      console.warn('refreshSelectedProduct called but selectedProduct or selectedProduct.id is undefined');
    }
  };

  const handleAdd = () => {
    if (!selectedProduct) {
      setError('Please select a product first');
      return;
    }
    
    setEditingMovement(null);
    setFormData({
      product_id: selectedProduct.id,
      source: '',
      movement_type: '',
      refno: '',
      comment: '',
      qty: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (movement) => {
    setEditingMovement(movement);
    // Show absolute value of qty in the form (always positive)
    const displayQty = Math.abs(movement.qty || 0);
    setFormData({
      product_id: movement.product_id || selectedProduct.id,
      source: movement.source || '',
      movement_type: movement.movement_type || '',
      refno: movement.refno || '',
      comment: movement.comment || '',
      qty: displayQty,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (movementId) => {
    if (window.confirm('Are you sure you want to delete this movement?')) {
      try {
        await productMovementsAPI.delete(movementId);
        
        // Add a longer delay to ensure backend operations are complete
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Store selected product to prevent losing it during async operations
        const currentSelectedProduct = selectedProduct;
        
        // Refresh both movements and product data to update current stock
        if (currentSelectedProduct && currentSelectedProduct.id) {
          console.log('Starting refresh after delete for product:', currentSelectedProduct.id);
          // Refresh product data first to update Current Stock
          await refreshSelectedProduct();
          // Small additional delay before refreshing movements
          await new Promise(resolve => setTimeout(resolve, 100));
          // Then refresh movements to update Balance column with fresh data
          await loadMovements(currentSelectedProduct.id);
        } else {
          console.warn('Cannot refresh after delete - currentSelectedProduct or ID is missing');
        }
      } catch (error) {
        setError(apiUtils.handleError(error));
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMovement(null);
    setFormData({
      product_id: '',
      source: '',
      movement_type: '',
      refno: '',
      comment: '',
      qty: '',
    });
    setError('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSourceChange = (e) => {
    const selectedSource = e.target.value;
    const selectedOption = movementOptions.find(option => option.source === selectedSource);
    
    setFormData({
      ...formData,
      source: selectedSource,
      movement_type: selectedOption ? selectedOption.movement_type : '',
    });
  };

  const handleSave = async () => {
    try {
      setError('');
      
      // Validation
      if (!formData.source || !formData.qty || !formData.refno) {
        setError('Source, reference number, and quantity are required');
        return;
      }

      if (parseFloat(formData.qty) <= 0) {
        setError('Quantity must be greater than 0');
        return;
      }

      // Store selected product to prevent losing it during async operations
      const currentSelectedProduct = selectedProduct;

      const movementData = {
        ...formData,
        qty: parseFloat(formData.qty),
      };

      let movementResponse;
      if (editingMovement) {
        movementResponse = await productMovementsAPI.update(editingMovement.id, movementData);
      } else {
        movementResponse = await productMovementsAPI.create(movementData);
      }

      handleCloseDialog();
      
      // Debug: Log the raw response to see the structure
      console.log('Raw API response:', movementResponse);
      
      // Check if the API returned updated product data
      // const responseData = apiUtils.formatResponse(movementResponse);
      // console.log('Formatted response data:', responseData);
      
      if (movementResponse && movementResponse.data.product) {
        console.log('Backend returned updated product data with on_hand:', movementResponse.data.product.on_hand);
        // Update the selected product with the fresh data from backend
        setSelectedProduct(movementResponse ? movementResponse.data.product : null);
        console.log('selectedProduct updated:', selectedProduct);
        
        // Also update the product in the products list
        setProducts(prevProducts => 
          prevProducts.map(p => 
            p.id === (movementResponse ? movementResponse.data.product.id : null) ? (movementResponse ? movementResponse.data.product : null) : p
          )
        );
      } else {
        // Fallback to the existing refresh method if backend doesn't return product data
        console.log('Backend did not return product data, using fallback refresh');
        await new Promise(resolve => setTimeout(resolve, 300));
        await refreshSelectedProduct();
      }
      
      // Refresh movements to update Balance column with fresh data
      if (currentSelectedProduct && currentSelectedProduct.id) {
        await loadMovements(currentSelectedProduct.id);
      }
    } catch (error) {
      setError(apiUtils.handleError(error));
    }
  };

  const getMovementTypeChip = (type) => {
    return (
      <Chip
        label={type}
        size="small"
        color={type === 'IN' ? 'success' : 'error'}
        variant="outlined"
      />
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center',
          mb: 3,
          flexShrink: 0,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Product Movements
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {/* Product Selection */}
      <Box sx={{ mb: 3, flexShrink: 0 }}>
        <Autocomplete
          options={products}
          getOptionLabel={(option) => `${option.name} (${option.sku || 'No SKU'})`}
          value={selectedProduct}
          onChange={(event, newValue) => {
            setSelectedProduct(newValue);
            setMovements([]);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Product"
              placeholder="Search for a product..."
              fullWidth
            />
          )}
          sx={{ maxWidth: 500 }}
        />
      </Box>

      {/* Movements Table */}
      {selectedProduct && (
        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6">
              Movements for: {selectedProduct.name}
            </Typography>
            <Chip
              label={`Current Stock: ${selectedProduct.on_hand ?? 0}`}
              color="primary"
              variant="outlined"
              size="medium"
              sx={{ fontWeight: 'medium' }}
            />
            {selectedProduct.sku && (
              <Chip
                label={`SKU: ${selectedProduct.sku}`}
                color="default"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
          
          <TableContainer 
            component={Paper} 
            sx={{ 
              height: 'calc(100vh - 420px)',
              '& .MuiTable-root': {
                minWidth: 650,
              },
              overflow: 'auto',
              mb: 2,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ref No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Comment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No movements found for this product
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((movement, index) => (
                    <TableRow key={movement.id} hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(movement)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(movement.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {movement.date_created 
                          ? format(new Date(movement.date_created), 'MMM dd, yyyy HH:mm')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>{movement.source}</TableCell>
                      <TableCell>{getMovementTypeChip(movement.movement_type)}</TableCell>
                      <TableCell>{movement.refno}</TableCell>
                      <TableCell>
                        <Box 
                          sx={{ 
                            fontWeight: 'medium',
                            color: movement.movement_type === 'IN' ? 'success.main' : 'error.main'
                          }}
                        >
                          {movement.movement_type === 'IN' ? '+' : '-'}{Math.abs(movement.qty)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {movement.on_hand ?? 0}
                        </Box>
                      </TableCell>
                      <TableCell>{movement.comment || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMovement ? 'Edit Movement' : 'Add Movement'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                name="source"
                value={formData.source}
                onChange={handleSourceChange}
                label="Source"
              >
                {movementOptions.map((option) => (
                  <MenuItem key={option.source} value={option.source}>
                    {option.source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.source && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Movement Type: {getMovementTypeChip(formData.movement_type)}
                </Typography>
              </Box>
            )}

            <TextField
              name="refno"
              label="Reference Number"
              value={formData.refno}
              onChange={handleInputChange}
              fullWidth
              required
            />

            <TextField
              name="qty"
              label="Quantity"
              type="number"
              value={formData.qty}
              onChange={handleInputChange}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
            />

            <TextField
              name="comment"
              label="Comment / Notes"
              value={formData.comment}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />

            
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingMovement ? 'Update' : 'Add'} Movement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductMovements;
